import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { TeamResponse, useDeleteTeam } from 'api';
import DeleteTeamDialog from 'pages/Team/DeleteTeamDialog';
import ErrorDialog from 'pages/Team/ErrorDialog';

import columnPredicate from './columnPredicate';
import RemoveTeamButton from './RemoveTeamButton';

type TeamsListProps = {
  teamsResponse: TeamResponse[];
  alwaysShowControls?: boolean;
  allowDelete?: boolean;
  handleItemDelete?: () => void;
  handlePageChange?: ({ lastPage }: { lastPage?: boolean }) => void;
};

const Container = styled('div')(({ theme }) => ({
  '> * + *': {
    marginTop: theme.spacing(spacingSizeMap.XS),
  },
  marginTop: '0',
}));

const TeamsList = ({
  handleItemDelete,
  teamsResponse,
  handlePageChange,
  alwaysShowControls = true,
  allowDelete = true,
}: TeamsListProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate, isPending, reset } = useDeleteTeam(queryClient);
  const [errorDialogOpen, setOpenErrorDialog] = useState(false);
  const [deleteTeam, setDeleteTeam] = useState<TeamResponse | null>(null);
  const [deleteTeamOpen, setDeleteTeamOpen] = useState<boolean>(false);

  const handleToDeleteTeam = (id: string) => {
    const team = teamsResponse.find(({ id: teamId }) => teamId === id);
    setDeleteTeam(team ?? null);
    setDeleteTeamOpen(!!team);
  };

  const handleConfirmDelete = (): void => {
    if (deleteTeam) {
      mutate(deleteTeam.id, {
        onSuccess: () => {
          if (handleItemDelete) {
            handleItemDelete();
            setDeleteTeam(null);
          }
        },
        onError: () => {
          setOpenErrorDialog(true);
        },
        onSettled: () => setDeleteTeamOpen(false),
      });
    }
  };

  const handleConfirmError = () => {
    setOpenErrorDialog(false);
    reset();
  };

  const dataStructure = {
    name: t('ui.event-management.teams.heading.name'),
    learnerCount: t('ui.event-management.teams.heading.total-learners'),
    organisationContext: t('ui.event-management.teams.organisation'),
  };

  const actions = [
    <RemoveTeamButton id="" key="" onClick={handleToDeleteTeam} />,
  ];

  return (
    <Container data-testid="teamslist-row">
      <DataTable
        items={teamsResponse}
        dataStructure={dataStructure}
        columnPredicate={columnPredicate}
        actions={allowDelete ? actions : undefined}
        onPageChange={handlePageChange}
        alwaysShowControls={alwaysShowControls}
        translations={{
          nextLabel: t('ui.common.pagination.next'),
          prevLabel: t('ui.common.pagination.prev'),
        }}
      />
      <DeleteTeamDialog
        key="delete-dialog"
        team={deleteTeam}
        open={deleteTeamOpen}
        onClose={() => setDeleteTeamOpen(false)}
        onConfirmDelete={handleConfirmDelete}
        deleting={isPending}
      />
      <ErrorDialog
        open={errorDialogOpen}
        onClose={handleConfirmError}
        title={t('ui.event-management.learners.teams.error-dialog.title')}
        body={t('ui.event-management.learners.teams.error-dialog.body', {
          teamName: deleteTeam?.name,
        })}
      />
    </Container>
  );
};
export default TeamsList;
